import type React from 'react';
import { forwardRef } from 'react';

import type { ColorName } from 'styles/config/colors';

import { cn } from 'utils/cn';
import { HoverButton } from '../HoverTarget';
import type { IconSize } from '../Icons/BaseIcon';
import LoadingIcon from '../LoadingIcon';

const LOADING_SIZE_CLASS: Record<string, string> = {
  xxs: 'w-[5px] h-[5px]',
  xs: 'w-xs3 h-xs3',
  sm: 'w-sm1 h-sm1',
  md: 'w-sm2 h-sm2',
  lg: 'w-sm3 h-sm3',
  xl: 'w-sm4 h-sm4',
  xxl: 'w-md1 h-md1',
  xxxl: 'w-md2 h-md2',
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  dataTestId?: string;
  disabled?: boolean;
  circle?: boolean;
  hoverTarget?: boolean;
  iconSize?: keyof typeof IconSize;
  hoverSize?: keyof typeof IconSize;
  hoverTargetColor?: ColorName;
  hoverIconColor?: ColorName;
  loading?: boolean;
  showFocusOutline?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      loading,
      disabled,
      circle,
      hoverIconColor,
      hoverTargetColor,
      hoverTarget = false,
      iconSize = 'md',
      hoverSize = 'xxl',
      className,
      type = 'button',
      showFocusOutline,
      style,
      ...rest
    },
    ref,
  ) => {
    return (
      <HoverButton
        ref={ref}
        $circle={circle}
        $hoverIconColor={hoverIconColor}
        $hoverSize={hoverSize}
        $hoverTarget={hoverTarget}
        $hoverTargetColor={hoverTargetColor}
        $iconSize={iconSize}
        $showFocusOutline={showFocusOutline}
        className={cn(
          'flex items-center justify-center',
          !circle && 'border-none',
          disabled && 'cursor-default opacity-50',
          className,
        )}
        disabled={loading || disabled}
        style={style}
        type={type}
        {...rest}
      >
        {loading ? (
          <span
            className={cn(
              'flex items-center justify-center',
              LOADING_SIZE_CLASS[iconSize],
            )}
          >
            <LoadingIcon />
          </span>
        ) : (
          children
        )}
      </HoverButton>
    );
  },
);

export default IconButton;
